import { NoddiIcon } from 'noddi-ui-common';
import { NoddiButton, NoddiDialog } from 'noddi-ui-web';
import { formatPhoneNumber } from 'noddi-util';
import { useState } from 'react';
import type { UserSessionData } from '../../contexts/AuthContext';
import { useAuthContext } from '../../contexts/useAuthContext';
import { ImpersonateUserDialog } from './ImpersonateUserDialog';

export const ImpersonatingBanner = ({
  onStopImpersonating,
  onImpersonated,
  defaultBrandDomain
}: {
  onStopImpersonating?: () => void;
  onImpersonated?: (userData: UserSessionData) => void;
  /** Customer-app brand domain — pre-selects brand when changing user. */
  defaultBrandDomain?: string;
}) => {
  const { impersonatedUser, clearImpersonatedUser, isSuperUser } = useAuthContext();
  const [changeUserOpen, setChangeUserOpen] = useState(false);

  if (!impersonatedUser) {
    return null;
  }

  const handleStop = () => {
    clearImpersonatedUser();
    onStopImpersonating?.();
  };

  const selectedUserGroup = impersonatedUser.user.userGroups?.find((ug) => ug.isSelected);

  return (
    <>
      <div className='relative z-docked flex w-full flex-wrap items-center justify-between gap-2 bg-yellow-500 px-4 py-2 text-sm text-black'>
        <div className='flex items-center gap-4'>
          <span className='hidden font-semibold md:block'>Impersonating</span>
          <span className='flex items-center gap-1'>
            <NoddiIcon name='User' size='small' />
            {impersonatedUser.user.firstName} {impersonatedUser.user.lastName}
          </span>
          {selectedUserGroup && (
            <span className='flex items-center gap-1'>
              <NoddiIcon name={selectedUserGroup.isOrganization ? 'Building2' : 'CircleUserRound'} size='small' />
              {selectedUserGroup.name}
            </span>
          )}
          {impersonatedUser.user.phoneNumber && (
            <span className='flex items-center gap-1'>
              <NoddiIcon name='Phone' size='small' />
              {formatPhoneNumber(impersonatedUser.user.phoneNumber)}
            </span>
          )}
        </div>
        <div className='flex items-center gap-2'>
          {isSuperUser && (
            <NoddiButton startIcon='User' size='extraSmall' variant='outline' onClick={() => setChangeUserOpen(true)}>
              Change user
            </NoddiButton>
          )}
          <NoddiButton startIcon='X' variant='outline' size='extraSmall' onClick={handleStop}>
            Stop
          </NoddiButton>
        </div>
      </div>

      <NoddiDialog
        open={changeUserOpen}
        onClose={() => setChangeUserOpen(false)}
        title='Impersonate user'
        description='Select a user from the list to view the application as them and take actions on their behalf'
        maxWidth='lg'
        className='h-[85vh] max-h-[85vh]'
        contentClassName='flex min-h-0 flex-1 flex-col overflow-hidden'
      >
        <ImpersonateUserDialog
          onClose={() => setChangeUserOpen(false)}
          onImpersonated={onImpersonated}
          defaultBrandDomain={defaultBrandDomain}
        />
      </NoddiDialog>
    </>
  );
};
